/**
 * Locale configuration for currency formatting
 */
export interface LocaleConfig {
  locale: string
  currency: string
}

/**
 * Predefined locale configurations
 */
export const LOCALE_CONFIGS: { [key: string]: LocaleConfig } = {
  US: { locale: 'en-US', currency: 'USD' },
  DE: { locale: 'de-DE', currency: 'EUR' },
  FR: { locale: 'fr-FR', currency: 'EUR' },
  PL: { locale: 'pl-PL', currency: 'PLN' },
  UK: { locale: 'en-GB', currency: 'GBP' },
  JP: { locale: 'ja-JP', currency: 'JPY' },
  CN: { locale: 'zh-CN', currency: 'CNY' },
  CH: { locale: 'de-CH', currency: 'CHF' },
  CA: { locale: 'en-CA', currency: 'CAD' },
  AU: { locale: 'en-AU', currency: 'AUD' }
}

/**
 * Get current locale configuration (can be extended to read from user settings)
 */
export const getCurrentLocaleConfig = (): LocaleConfig => {
  // Default to German locale for Trialytix
  // This could be extended to read from user preferences or environment
  return LOCALE_CONFIGS.DE
}

/**
 * Convert number to correct currency format with symbol at the front
 * IMPORTANT: This function ensures the currency symbol is at the FRONT for backward compatibility
 * with existing code that expects the format "€3.582,83" instead of "3.582,83 €"
 *
 * @param amount - The numeric amount to format
 * @param currency - Currency code (e.g., 'EUR', 'USD')
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @return string - Formatted currency string with symbol at front (e.g., "€3.582,83 ")
 */
export const currencyFormat = (
  amount: number,
  currency: string = getCurrentLocaleConfig().currency,
  locale: string = getCurrentLocaleConfig().locale
): string => {
  // Handle edge cases
  if (isNaN(amount) || amount === null || amount === undefined) {
    amount = 0
  }

  // Use toLocaleString for formatting
  const _amount = new Number(amount).toLocaleString(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  // Extract currency symbol and ensure it's at the front
  // Different locales place symbol differently:
  // - German locale: "3.582,83 €" (symbol at end)
  // - US locale: "$3,582.83" (symbol already at front)
  const symbolMatch = _amount.match(/[^\d.,\s-]+/)
  if (!symbolMatch) {
    return _amount
  }

  const symbol = symbolMatch[0]
  const numberPart = _amount.replace(symbol, '').trim()

  // Return with symbol at front
  return symbol + numberPart + ' '
}

/**
 * Parse a formatted currency string back to a number
 * @param formattedAmount - The formatted currency string
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @return number - The numeric value
 */
export const parseCurrencyAmount = (
  formattedAmount: string, 
  locale: string = getCurrentLocaleConfig().locale
): number => {
  // Remove currency symbols and spaces
  let cleanedAmount = formattedAmount.replace(/[^\d.,\-]/g, '')
  
  // Handle different decimal separators based on locale
  if (locale.includes('de') || locale.includes('fr') || locale.includes('pl')) {
    // European format: 1.234,56
    cleanedAmount = cleanedAmount.replace(/\./g, '').replace(',', '.')
  } else {
    // US/UK format: 1,234.56
    cleanedAmount = cleanedAmount.replace(/,/g, '')
  }
  
  return parseFloat(cleanedAmount) || 0
}

/**
 * Format currency without decimals for display with symbol at the front
 * IMPORTANT: This function ensures the currency symbol is at the FRONT for backward compatibility
 *
 * @param amount - The numeric amount to format
 * @param currency - Currency code
 * @param locale - Locale string
 * @return string - Formatted currency string without decimals, symbol at front
 */
export const currencyFormatNoDecimals = (
  amount: number,
  currency: string = getCurrentLocaleConfig().currency,
  locale: string = getCurrentLocaleConfig().locale
): string => {
  // Handle edge cases
  if (isNaN(amount) || amount === null || amount === undefined) {
    amount = 0
  }

  // Use toLocaleString for formatting
  const _amount = new Number(amount).toLocaleString(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

  // Extract currency symbol and ensure it's at the front
  const symbolMatch = _amount.match(/[^\d.,\s-]+/)
  if (!symbolMatch) {
    return _amount
  }

  const symbol = symbolMatch[0]
  const numberPart = _amount.replace(symbol, '').trim()

  // Return with symbol at front
  return symbol + numberPart + ' '
}